import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { buildPhotoUrl, formatTime as formatMessageTime } from "./utils";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import { Button } from "src/components/ui/button";
import { Textarea } from "src/components/ui/textarea";
import { Dialog, DialogContent } from "src/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { ScrollArea } from "src/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog";
import { cn } from "src/shared/lib/utils/utils";
import {
  User,
  Info,
  Trash2,
  Image as ImageIcon,
  Upload,
  X,
  Copy,
  CheckCircle2,
  MoreVertical,
  Download,
  ArrowUp,
  Plus,
  FileText,
  Mic,
  Video as VideoIcon,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const FILE_MESSAGE_TYPES = new Set(["VOICE", "VIDEO", "DOCUMENT"]);
const DIRECT_MEDIA_URL_RE = /(https?:\/\/[^\s\]]+)/i;

const getMessageType = (message) =>
  String(message?.message_type || "TEXT")
    .split(".")
    .pop()
    .toUpperCase();

const isMediaPlaceholderText = (content) => {
  if (!content || typeof content !== "string") return false;
  const normalized = content.trim().toLowerCase();
  return (
    normalized.startsWith("[voice message") ||
    normalized.startsWith("[video") ||
    normalized.startsWith("[file") ||
    normalized.startsWith("[document") ||
    normalized === "[voice]" ||
    normalized === "[video]" ||
    normalized === "[file]" ||
    normalized === "[document]" ||
    normalized === "[изображение]" ||
    normalized === "[голосовое сообщение]"
  );
};

const normalizeLocalhostUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.hostname === 'localhost' && parsed.port === '3000') {
      parsed.port = '';
      return parsed.toString();
    }
  } catch {
    // игнорируем ошибки парсинга
  }
  return url;
};

const useInterval = (callback, delay) => {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    if (delay !== null) {
      const id = setInterval(() => savedCallback.current(), delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const resolveMediaUrl = (message) => {
  // Сначала проверяем content на маркер видео MAX
  if (typeof message?.content === "string" && message.content.startsWith("max_video_token:")) {
    const token = message.content.split(":")[1];
    const authToken = new URLSearchParams(window.location.search).get("token");
    return `/api/v1/chats/max/video/${token}?token=${authToken}`;
  }

  // Далее стандартная логика
  const directUrlMatch =
    typeof message?.content === "string"
      ? message.content.match(DIRECT_MEDIA_URL_RE)
      : null;
  let rawUrl = message?.file_url || message?.image_url || directUrlMatch?.[1];
  if (!rawUrl || typeof rawUrl !== "string") return null;

  if (rawUrl.startsWith("max_video_token:")) {
    const token = rawUrl.split(":")[1];
    const authToken = new URLSearchParams(window.location.search).get("token");
    return `/api/v1/chats/max/video/${token}?token=${authToken}`;
  }

  return buildPhotoUrl(rawUrl) || rawUrl;
};

const getMediaMeta = (messageType) => {
  switch (messageType) {
    case "VOICE":
      return { title: "Голосовое сообщение", Icon: Mic };
    case "VIDEO":
      return { title: "Видео", Icon: VideoIcon };
    case "DOCUMENT":
      return { title: "Файл", Icon: FileText };
    default:
      return { title: "Вложение", Icon: FileText };
  }
};

function ChatWindow({
  selectedChat,
  messages,
  messageLoading,
  messagesEndRef,
  formatTime,
  onAvatarClick,
  onDeleteMessage,
  onSendMessage,
  onSendImage,
  onSendTypingIndicator,
  typingStatus,
  onlineStatus,
  operatorTyping,
  isMobile,
  onBackToList,
}) {
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [copied, setCopied] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(null);

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(Date.now());
  useInterval(() => setCurrentTime(Date.now()), 1000);

  const lastActivity = selectedChat?.contact?.last_activity;
  const now = currentTime;
  const isOnline = lastActivity && now - lastActivity <= 10000;

  const formatLastSeen = useCallback(
    (lastActivity) => {
      if (!lastActivity) return null;
      const diff = now - lastActivity;
      if (diff <= 10000) return "онлайн";
      const minutes = Math.floor(diff / 60000);
      if (minutes < 60) return `был(а) ${minutes} мин назад`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `был(а) ${hours} ч назад`;
      const days = Math.floor(hours / 24);
      return `был(а) ${days} дн назад`;
    },
    [now],
  );

  const lastSeenText = formatLastSeen(lastActivity);

  // effectiveTyping теперь только для оператора
  const effectiveTyping = operatorTyping;

  const handleMessageChange = useCallback(
    (e) => {
      const value = e.target.value;
      setNewMessage(value);

      if (onSendTypingIndicator) {
        onSendTypingIndicator(value.trim().length > 0);
      }
    },
    [onSendTypingIndicator],
  );

  const handleMessageBlur = useCallback(() => {
    if (onSendTypingIndicator) {
      onSendTypingIndicator(false);
    }
  }, [onSendTypingIndicator]);

  const handleDeleteMessage = useCallback(
    async (message) => {
      if (!message?.id) return;

      try {
        if (onDeleteMessage) {
          await onDeleteMessage(message);
        }
        setMessageToDelete(null);
        setDeleteAlertOpen(false);
      } catch (error) {
        console.error("Ошибка удаления сообщения:", error);
      }
    },
    [onDeleteMessage],
  );

  const handleSend = useCallback(async () => {
    if (!newMessage.trim() && !file) return;

    if (onSendTypingIndicator) {
      onSendTypingIndicator(false);
    }

    try {
      if (file) {
        setIsUploading(true);

        if (onSendImage) {
          await onSendImage(file);
        }

        setFile(null);
        setIsUploading(false);

        if (newMessage.trim()) {
          if (onSendMessage) {
            await onSendMessage(newMessage);
          }
        }
      } else {
        if (onSendMessage) {
          await onSendMessage(newMessage);
        }
      }

      setNewMessage("");
    } catch (error) {
      console.error("Ошибка отправки сообщения:", error);
      setIsUploading(false);
      toast.error("Ошибка отправки сообщения");
    }
  }, [newMessage, file, onSendTypingIndicator, onSendImage, onSendMessage]);

  const handleCopyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Ошибка копирования:", err);
    }
  }, []);

  const handleOpenFile = useCallback((url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleDownloadFile = useCallback((url, filename = "attachment") => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFile = useCallback(
    (selectedFile) => {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

      if (!allowedTypes.includes(selectedFile.type.toLowerCase())) {
        if (
          String(selectedChat?.channel_type || "").toUpperCase() === "AVITO"
        ) {
          toast.error(
            "В чатах Avito можно отправлять только текст и изображения",
          );
        } else {
          toast.error(
            "Недопустимый формат файла. Выберите изображение в формате JPEG, JPG или PNG",
          );
        }
        return false;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("Файл слишком большой. Максимальный размер - 10MB");
        return false;
      }

      setFile(selectedFile);
      return true;
    },
    [selectedChat?.channel_type],
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const selectedFiles = Array.from(e.target.files);
      if (selectedFiles.length > 0) {
        handleFile(selectedFiles[0]);
      }
      e.target.value = "";
    },
    [handleFile],
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        handleFile(droppedFiles[0]);
      }
    },
    [handleFile],
  );

  const openImageModal = (url) => {
    setModalImageUrl(url);
    setModalVisible(true);
  };

  const handleMessageClick = (messageId, isOperator) => {
    if (isOperator) {
      setPopoverOpen(messageId === popoverOpen ? null : messageId);
    }
  };

  const renderMessage = (message) => {
    const isOperator =
      String(message.sender_type || "").toUpperCase() === "OPERATOR";
    const messageType = getMessageType(message);
    const isMediaFile = FILE_MESSAGE_TYPES.has(messageType);
    const contentAsImage =
      messageType === "IMAGE" &&
      message.content &&
      (message.content.startsWith("data:image") ||
        message.content.startsWith("http://") ||
        message.content.startsWith("https://"));
    const contentAsPhotoPath =
      messageType === "IMAGE" &&
      message.content &&
      typeof message.content === "string" &&
      (message.content.startsWith("photos/") ||
        message.content.startsWith("api/v1/photos/"));
    const imageUrl = contentAsPhotoPath
      ? buildPhotoUrl(message.content)
      : contentAsImage
        ? message.content
        : resolveMediaUrl(message);
    const fileUrl = isMediaFile ? resolveMediaUrl(message) : null;
    const normalizedImageUrl = normalizeLocalhostUrl(imageUrl);
    const normalizedFileUrl = normalizeLocalhostUrl(fileUrl);
    const mediaMeta = getMediaMeta(messageType);
    const previewText =
      message.content && !isMediaPlaceholderText(message.content)
        ? message.content
        : null;

    return (
      <div
        key={message.id}
        className={cn(
          "flex items-end gap-2 mb-4 px-4 h-full",
          isOperator ? "justify-end" : "justify-start",
        )}
        onClick={() => handleMessageClick(message.id, isOperator)}
      >
        {!isOperator && (
          <Avatar className="h-8 w-8">
            <AvatarImage src={selectedChat?.contact?.avatar} />
            <AvatarFallback className="bg-gray-200 text-gray-800">
              {selectedChat?.contact?.name?.[0] || "U"}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="flex flex-col max-w-[60%]">
          {!isOperator && (
            <span className="text-xs text-gray-500 mb-1 ml-1">
              {selectedChat?.contact?.name || "Пользователь"}
            </span>
          )}

          <div className="relative">
            <div
              className={cn(
                "px-4 py-3 rounded-2xl relative group",
                isOperator
                  ? "bg-gray-900 text-white rounded-br-md"
                  : "bg-gray-100 text-gray-900 rounded-bl-md",
              )}
            >
              {message.content && messageType !== "IMAGE" && !isMediaFile && (
                <div className="text-sm leading-relaxed whitespace-pre-wrap pr-6 break-all">
                  {message.content}
                </div>
              )}

              {messageType === "IMAGE" && (
                <div className="mt-3 mx-4 sm:mx-0">
                  {normalizedImageUrl ? (
                    <>
                      <img
                        src={normalizedImageUrl}
                        alt=""
                        onError={(e) => {
                          const img = e.target;
                          const retryCount = parseInt(
                            img.dataset.retry || "0",
                            10,
                          );
                          if (retryCount < 2) {
                            img.dataset.retry = String(retryCount + 1);
                            const delay = retryCount === 0 ? 1500 : 3500;
                            setTimeout(() => {
                              img.src = normalizedImageUrl;
                            }, delay);
                            return;
                          }
                          img.style.display = "none";
                          const fallback = img.nextElementSibling;
                          if (fallback) fallback.style.display = "block";
                        }}
                        onClick={() => openImageModal(normalizedImageUrl)}
                        className={cn(
                          "max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity",
                          "object-cover block",
                          "w-full sm:w-auto sm:max-w-[280px]",
                        )}
                      />
                      <span
                        className="text-xs text-gray-500 hidden"
                        style={{ display: "none" }}
                      >
                        Изображение недоступно
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">
                      Изображение недоступно
                    </span>
                  )}
                </div>
              )}

              {isMediaFile && (
                <div className="mt-1 space-y-3 min-w-[220px]">
                  <div
                    className={cn(
                      "rounded-xl border px-3 py-3",
                      isOperator
                        ? "border-white/15 bg-white/5"
                        : "border-black/10 bg-white/70",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <mediaMeta.Icon className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">
                            {mediaMeta.title}
                          </div>
                          {previewText && (
                            <div
                              className={cn(
                                "text-xs mt-1 break-all",
                                isOperator ? "text-gray-300" : "text-gray-500",
                              )}
                            >
                              {previewText}
                            </div>
                          )}
                        </div>
                      </div>

                      {normalizedFileUrl ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-7 w-7",
                              isOperator
                                ? "text-gray-200 hover:bg-white/10 hover:text-white"
                                : "hover:bg-black/5",
                            )}
                            onClick={() => handleOpenFile(normalizedFileUrl)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-7 w-7",
                              isOperator
                                ? "text-gray-200 hover:bg-white/10 hover:text-white"
                                : "hover:bg-black/5",
                            )}
                            onClick={() =>
                              handleDownloadFile(
                                normalizedFileUrl,
                                `${messageType.toLowerCase()}_${message.id || Date.now()}`,
                              )
                            }
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className={cn(
                            "flex items-center gap-1 text-xs shrink-0",
                            isOperator ? "text-gray-400" : "text-gray-500",
                          )}
                        >
                          <AlertCircle className="h-3.5 w-3.5" />
                          Недоступно
                        </div>
                      )}
                    </div>

                    {messageType === "VOICE" && normalizedFileUrl && (
                      <audio
                        controls
                        preload="metadata"
                        src={normalizedFileUrl}
                        className="w-full mt-3"
                      />
                    )}

                    {messageType === "VIDEO" && normalizedFileUrl && (
                      <video
                        controls
                        preload="metadata"
                        src={normalizedFileUrl}
                        className="w-full mt-3 rounded-lg bg-black"
                      />
                    )}
                  </div>
                </div>
              )}

              <div
                className={cn(
                  "text-xs mt-2",
                  isOperator ? "text-gray-400" : "text-gray-500",
                )}
              >
                {formatMessageTime(message.created_at)}
              </div>
            </div>

            {isOperator && popoverOpen === message.id && (
              <Popover open={true} onOpenChange={() => setPopoverOpen(null)}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "absolute top-2 right-2 h-6 w-6 p-0",
                      isOperator
                        ? "bg-gray-800 hover:bg-gray-700 text-gray-300"
                        : "bg-gray-200 hover:bg-gray-300",
                    )}
                  >
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="end">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm h-8 px-2"
                    onClick={() => handleCopyText(message.content)}
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                        Скопировано
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-2" />
                        Копировать
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setMessageToDelete(message);
                      setDeleteAlertOpen(true);
                      setPopoverOpen(null);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Удалить
                  </Button>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {isOperator && (
          <Avatar className="h-8 w-8">
            <AvatarImage src={selectedChat?.operator_avatar} />
            <AvatarFallback className="bg-gray-900 text-white">
              OP
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    );
  };

  const sortedMessages = useMemo(() => {
    return [...messages].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  }, [messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, messagesEndRef]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div
      className="flex flex-col h-full bg-white relative overflow-auto"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      ref={dropZoneRef}
    >
      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-lg z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
            <Upload className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <div className="font-semibold text-blue-600 mb-2">
              Перетащите изображение сюда
            </div>
            <div className="text-sm text-gray-500">
              Только JPEG, JPG, PNG
              <br />
              Максимальный размер: 10MB
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b px-6 py-3 flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar
              className="h-14 w-14 border cursor-pointer"
              onClick={onAvatarClick}
            >
              <AvatarImage src={selectedChat?.contact?.avatar} />
              <AvatarFallback className="bg-gray-200 text-gray-800">
                <User className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="flex flex-col">
            <div className="font-semibold text-gray-900">
              {selectedChat?.contact?.name || selectedChat?.name || "Без имени"}
            </div>
            {isOnline ? (
              <span className="text-xs text-gray-500">онлайн</span>
            ) : lastSeenText ? (
              <span className="text-xs text-gray-500">{lastSeenText}</span>
            ) : null}
            {effectiveTyping && (
              <div className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
                <span>
                  Вы печатаете
                  <span className="inline-flex ml-0.5">
                    <span className="animate-bounce [animation-delay:0ms]">
                      .
                    </span>
                    <span className="animate-bounce [animation-delay:150ms]">
                      .
                    </span>
                    <span className="animate-bounce [animation-delay:300ms]">
                      .
                    </span>
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onAvatarClick}
                className="h-[100%] w-12 hover:bg-gray-100"
              >
                <Info className="h-[100%] w-16 text-gray-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Информация о чате</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <ScrollArea className="flex-1 px-4 py-4 overflow-auto max-w-full">
        {messageLoading && (
          <div className="flex justify-center py-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
              <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" />
              <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.1s]" />
              <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
              <span className="text-sm text-gray-500 ml-2">
                Загрузка сообщений...
              </span>
            </div>
          </div>
        )}

        {sortedMessages.map(renderMessage)}

        <div ref={messagesEndRef} />
      </ScrollArea>

      <Dialog open={modalVisible} onOpenChange={setModalVisible}>
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] p-0 border-none bg-transparent shadow-none"
          showCloseButton={false}
        >
          <div className="relative w-full h-full flex items-center justify-center p-8">
            <div className="relative max-w-full max-h-full group">
              <div className="relative overflow-hidden rounded-lg shadow-2xl">
                <img
                  src={modalImageUrl}
                  alt="Превью"
                  className="max-w-full max-h-full object-contain block"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 pointer-events-none" />
              </div>

              <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 bg-background/30 hover:bg-background/70 shadow-lg backdrop-blur-sm"
                  onClick={() => setModalVisible(false)}
                >
                  <X className="h-4 w-4" />
                </Button>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        className="h-8 w-8 bg-background/30 hover:bg-background/70 shadow-lg backdrop-blur-sm"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = modalImageUrl;
                          link.download = `attachment_${Date.now()}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Скачать</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {file && (
        <div className="bg-white border-t px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-gray-700">Изображение</span>
              <span className="text-sm text-gray-500">
                ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveFile}
              disabled={isUploading}
              className="h-8 text-gray-600 hover:text-gray-900"
            >
              <X className="h-3 w-3 mr-1" />
              Удалить
            </Button>
          </div>
          <div className="flex justify-center">
            <img
              src={URL.createObjectURL(file)}
              alt="preview"
              className="w-24 h-24 rounded-lg object-cover"
            />
          </div>
          {isUploading && (
            <div className="mt-3">
              <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 animate-pulse"></div>
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">
                Отправка изображения...
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white px-4 py-3 border-t">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative bg-gray-100 rounded-xl border border-gray-300 hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
            <Textarea
              placeholder={
                file
                  ? "Добавьте текст к изображению..."
                  : "Введите сообщение..."
              }
              value={newMessage}
              onChange={handleMessageChange}
              onBlur={handleMessageBlur}
              onKeyDown={handleKeyDown}
              className="flex-1 h-1 min-h-[38px] max-h-50 bg-transparent border-0 focus-visible:ring-0 resize-none text-sm px-3 py-2 pr-12 pl-7"
              disabled={isUploading}
              rows={1}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    onClick={handleFileSelect}
                    disabled={!!file || isUploading}
                    className="h-8 w-8 bg-transparent hover:bg-transparent hover:text-gray-600 absolute bottom-1"
                  >
                    <Plus className="h-4 w-4 text-black" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Прикрепить файл</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileInputChange}
            />

            <Button
              type="button"
              onClick={handleSend}
              disabled={(!newMessage.trim() && !file) || isUploading}
              size="icon"
              className="absolute right-2 bottom-1 h-8 w-7 bg-transparent hover:bg-transparent text-black"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить сообщение?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Сообщение будет удалено навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 hover:bg-gray-100">
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteMessage(messageToDelete)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default React.memo(ChatWindow);
